import { useIntl } from 'react-intl';

import { Icon } from '@verantos-dev/shared-components';
import { StagedChangesPill } from '@verantos-dev/shared-ui-components';

import type { ConceptGroupExportPillProps } from '../lib/types/conceptGroupExport';

/**
 * The pill carrying the concept-group bulk export (OR-6580). Its count doubles as
 * the cap notice: `N selected`, then `N of M selected` once the cap is reached.
 */
export const ConceptGroupExportPill = ({
  selectedCount,
  selectableCount,
  max,
  isInFlight,
  onClear,
  onExport,
}: ConceptGroupExportPillProps) => {
  const { formatMessage } = useIntl();
  const atLimit = selectedCount >= max;
  // Floored at the cap, so a selection narrowed by search afterwards still reads
  // `50 of 50` rather than a total smaller than the count.
  const total = Math.max(selectableCount, max);

  return (
    <StagedChangesPill
      visible={selectedCount > 0}
      // Anchored to the artifact list, so it centers over the grid rather than the
      // chat panel. Draggable off whichever rows it covers, like the other pills.
      position="absolute"
      draggable
      message={formatMessage(
        {
          id: atLimit ? 'conceptGroups.export.selectionCount' : 'conceptGroups.export.pillCount',
        },
        { count: selectedCount, total }
      )}
      secondaryButton={{
        label: formatMessage({ id: 'conceptGroups.export.selectionClear' }),
        onClick: onClear,
        'data-testid': 'concept-group-export-pill-clear',
      }}
      primaryButton={{
        label: formatMessage({ id: 'conceptGroups.export.selectionSubmit' }),
        onClick: onExport,
        // Drawn 0..18 with no padding, so the inset viewBox centres it on 20px.
        // `currentColor` because Icon's default fill ignores the disabled state.
        startIcon: (
          <Icon
            id="export-concept-groups"
            icon="export"
            viewBox="-3 -3 24 24"
            width={20}
            height={20}
            sx={{ fill: 'currentColor' }}
          />
        ),
        // The spinner separates "busy" from "broken"; the tooltip says which.
        loading: isInFlight,
        tooltip: isInFlight ? formatMessage({ id: 'conceptGroups.export.inProgress' }) : undefined,
        'data-testid': 'concept-group-export-pill-submit',
      }}
      data-testid="concept-group-export-pill"
    />
  );
};
